/** The card a dashboard-band chart sits in (ADR-0212, P7): a title, its unit, a Table button, and
 * the chart.
 *
 * - **The unit is its own prop** and is printed muted at the right of the title row, as the caller
 *   wrote it (`tCO2e`, never upper-cased): a chart that does not say what its numbers are in has not
 *   said much. It is text beside the title, not a heading, so the page's heading outline is
 *   unchanged.
 * - **The Table button** (`aria-pressed`) swaps the drawing for a real `<table>` of the same series,
 *   for anyone: a reader who prefers numbers, a screen reader, a person who wants to copy a figure.
 *   The chart components read the choice from `ChartViewContext` and draw their own `ChartTable`
 *   visibly, so the two cannot disagree. A chart that is itself a table (the coverage grid) passes
 *   `hasTable={false}` and no button is drawn.
 * - The card draws no figure of its own and computes nothing.
 */

import { useId, useState } from "react";
import type { ReactNode } from "react";

import styles from "./ChartCard.module.css";
import { ChartViewContext } from "./chartView";

export function ChartCard({
  title,
  unit,
  children,
  hasTable = true,
  action,
  testId = "chart-card",
}: {
  title: string;
  /** What the numbers are in, as written: `tCO2e`, `documents`, `%`. */
  unit?: string;
  children: ReactNode;
  hasTable?: boolean;
  /** One quiet link at the card's foot corner, built by the caller (a router `Link`). */
  action?: ReactNode;
  testId?: string;
}) {
  const [asTable, setAsTable] = useState(false);
  const titleId = useId();
  return (
    <section className={`card ${styles.card}`} aria-labelledby={titleId} data-testid={testId} data-chart>
      <div className={styles.head}>
        <span id={titleId} className={styles.title}>
          {title}
        </span>
        {unit ? (
          <span className={styles.unit} data-testid="chart-unit">
            {unit}
          </span>
        ) : null}
        {hasTable ? (
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={asTable}
            onClick={() => {
              setAsTable((now) => !now);
            }}
          >
            Table
          </button>
        ) : null}
      </div>
      <ChartViewContext.Provider value={asTable}>{children}</ChartViewContext.Provider>
      {action ? <div className={styles.action}>{action}</div> : null}
    </section>
  );
}
